import React from 'react'
import styled from 'styled-components'
import { MapPin } from 'lucide-react'
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  fadeInUp,
  staggerContainer,
  smoothEase,
} from './animations/AnimatedSection'

const cardVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.92 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: smoothEase },
  },
}

const Vision2030Section = () => {
  const visions = [
    {
      image: '/images/homepage-new/cleaning.webp',
      location: 'Bali, Indonesia',
      value: '1 Billion',
      label: 'Verified Micro-Actions',
    },
    {
      image: '/images/homepage-new/water.webp',
      location: 'Nairobi, Kenya',
      value: '17 SDGs',
      label: 'Activated Globally',
    },
    {
      image: '/images/homepage-new/study.webp',
      location: 'Bogotá, Colombia',
      value: 'Real-Time',
      label: 'Civic Intelligence Dashboard',
    },
  ]

  return (
    <Section>
      <Header>
        <AnimatedSection variants={fadeInUp}>
          <Title>
            Imagine This by <Highlight>2030</Highlight>
          </Title>
        </AnimatedSection>
        <AnimatedSection variants={fadeInUp} delay={0.15}>
          <Subtitle>
            Our vision for a connected, action-driven world where every person
            contributes to global sustainability.
          </Subtitle>
        </AnimatedSection>
      </Header>

      <StaggerContainer
        variants={staggerContainer}
        style={{
          display: 'flex',
          flexDirection: 'row' as const,
          justifyContent: 'center',
          alignItems: 'center',
          gap: '24px',
          width: '100%',
          maxWidth: '930px',
          flexWrap: 'wrap' as const,
        }}
      >
        {visions.map((vision, idx) => (
          <StaggerItem key={idx} variants={cardVariants}>
            <VisionCard>
              <CardImage src={vision.image} alt={vision.value} />
              <Overlay />
              <CardContent>
                <LocationRow>
                  <MapPin size={20} color="#BBE148" />
                  <LocationText>{vision.location}</LocationText>
                </LocationRow>
                <ValueText>{vision.value}</ValueText>
                <LabelText>{vision.label}</LabelText>
              </CardContent>
            </VisionCard>
          </StaggerItem>
        ))}
      </StaggerContainer>

      <AnimatedSection variants={fadeInUp} delay={0.3}>
        <FooterText>
          By combining AI intelligence, expert knowledge, and youth energy,
          we&apos;re building the infrastructure for global action at scale.
          Join us in making this vision a reality.
        </FooterText>
      </AnimatedSection>
    </Section>
  )
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 140px 24px;
  gap: 48px;
  width: 100%;
  background: #f8f7f0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 60px 24px;
    gap: 32px;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 792px;
  width: 100%;
`

const Title = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 48px;
  line-height: 56px;
  text-align: center;
  letter-spacing: -0.04em;
  color: #000000;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
  }
`

const Highlight = styled.span`
  background: linear-gradient(90deg, #0e6409 0%, #bbe148 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`

const Subtitle = styled.p`
  width: 100%;
  max-width: 600px;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: #5e5e5e;
  margin: 0;
`

const VisionCard = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 289.75px;
  height: 362.19px;
  border-width: 1px 4px 4px 1px;
  border-style: solid;
  border-color: #2a2a2a;
  border-radius: 16px;
  overflow: hidden;
  flex-shrink: 0;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0px 20px 40px -10px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 400px;
  }
`

const CardImage = styled.img.attrs({ loading: 'lazy' })`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.4) 50%,
    rgba(0, 0, 0, 0) 100%
  );
`

const CardContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const LocationRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const LocationText = styled.span`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  color: #ffffff;
`

const ValueText = styled.div`
  font-family: 'Roboto', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 30px;
  line-height: 36px;
  color: #bbe148;
`

const LabelText = styled.div`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: rgba(255, 255, 255, 0.8);
`

const FooterText = styled.p`
  max-width: 667px;
  width: 100%;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: #5e5e5e;
  margin: 0;
`

export default Vision2030Section
